import React, { useCallback, useEffect, useState } from 'react'
import LogosVersion2 from './Logo2'
import { useDispatch, useSelector } from 'react-redux'
import { fetchToken, getUserRole, logoutUser, selectAuthentication, setAuthenticationError, setUserEmail } from '../redux/authenticationSlice'
import { ClipLoader, SyncLoader } from 'react-spinners';
import { useLocation, useNavigate } from 'react-router-dom'
import { Mixpanel } from '../config/Mixpanel';
import constants from '../utilities/constants';
import { fetchCourseList, updateCourseName } from '../redux/courseListSlice';
import { checkTrialPeriod } from '../redux/nameSlice';
import { toast } from 'react-toastify';
import { setUTMDetails } from '../redux/skillSlice';
import { ArrowRight, LockKeyhole, Mail, TriangleAlert } from 'lucide-react';

const Welcome = () => {

    const location = useLocation()

    const params = new URLSearchParams(location.search)
    const emailID = params.get('email')
    const utm_source = params.get('utm_source')
    const utm_medium = params.get('utm_medium')
    const utm_campaign = params.get('utm_campaign')

    const userEmail = useSelector(selectAuthentication)?.email
    const [ email, setEmail ] = useState(userEmail)
    const [ loading, setLoading ] = useState(false)
    const [ welcomePageLoading, setWelcomePageLoading ] = useState(false)
        
    const error = useSelector(selectAuthentication)?.error
    
    const dispatch = useDispatch()
    const navigate = useNavigate()
    

    useEffect(() => {
        if (utm_source && utm_medium) {
            Mixpanel.people.set({
                'utm_source': utm_source,
                'utm_medium': utm_medium,
            })
            dispatch(setUTMDetails({ utm_source: utm_source ? utm_source : "", utm_medium: utm_medium ? utm_medium : "", utm_campaign: utm_campaign ? utm_campaign : "" }))
        }
    }, [dispatch, utm_source, utm_medium, utm_campaign])

    const newLearners = useCallback(() => {
        const time = new Date()
        const hours = time.getHours()
        const minutes = time.getMinutes()
        let maxLearners = hours.toString() + (minutes < 9 ? '0' + minutes.toString() : minutes.toString())
        return Math.max(parseInt(maxLearners)+9, 171)
    }, [])

    const learnerCount = newLearners()

    const getTargetCourse = useCallback((courses) => {
        const enrolledCourses = courses?.filter((course) => course?.course_id) || []
        return enrolledCourses[0]
    }, [])

    const helperFunction = useCallback(async (email, course) => {
        try {
            await dispatch(checkTrialPeriod({ email: email, subject: course.course_name })).unwrap()
            await dispatch(getUserRole({ email: email })).unwrap()
            dispatch(updateCourseName(course))
            navigate(`/${encodeURIComponent(course.course_name)}`)
        } catch (error) {
            console.log(error.message);
            toast.error("Something went wrong! Please try again later.")
            navigate('/welcome', { replace: true })
            return
        }
    }, [dispatch, navigate])
    
    useEffect(() => {
        dispatch(logoutUser())
        Mixpanel.track(constants.Mixpanel.WELCOME_PAGE)
        if (emailID){
            setWelcomePageLoading(true)
            const fetchData = async () => {
                dispatch(setUserEmail(emailID))
                try {
                    const response = await dispatch(fetchToken({ email_id: emailID })).unwrap()
                    if (response?.access_token){
                        Mixpanel.identify(emailID)
                        const courseResponse = await dispatch(fetchCourseList()).unwrap()
                        const targetCourse = getTargetCourse(courseResponse?.courses)
                        if (!targetCourse) {
                            toast.error("No enrolled courses found! Please try again later.")
                            navigate('/welcome', { replace: true })
                            return
                        }
                        await helperFunction(emailID, targetCourse)
                    }
                }catch (err) {
                    console.log(err.message);
                }finally{
                    setWelcomePageLoading(false)
                }
            }
            fetchData()
        }
    }, [dispatch, emailID, getTargetCourse, helperFunction, navigate])

    const handleSubmit = (e) => {
        e.preventDefault()
        Mixpanel.track(constants.Mixpanel.WELCOME_PAGE_BUTTON_CLICKED)
        setLoading(true)
        if (error) dispatch(setAuthenticationError(null))

        const fetchData = async () => {
            dispatch(setUserEmail(email))
            try {
                const response = await dispatch(fetchToken({ email_id: email })).unwrap()
                if (response?.access_token){
                    const courseResponse = await dispatch(fetchCourseList()).unwrap()
                    const targetCourse = getTargetCourse(courseResponse?.courses)
                    if (!targetCourse) {
                        toast.error("No enrolled courses found! Please try again later.")
                        navigate('/welcome', { replace: true })
                        return
                    }
                    await helperFunction(email, targetCourse)
                }
            }catch (err) {
                console.log(err.message);
            }finally{
                setLoading(false)
            }
        }
        fetchData()
    }

    const handleCreateCourse = () => {
        Mixpanel.track(constants.Mixpanel.WELCOME_PAGE_BUTTON_CLICKED)
        dispatch(setAuthenticationError(null))
        dispatch(setUserEmail(null))
        navigate('/')
    }

    const handleInputChange = (e) => {
        if (error) dispatch(setAuthenticationError(null))
        setEmail(e.target.value)
    }

    return (
        <>
            {!welcomePageLoading ? <div className='welcome-page relative min-h-full w-full flex-1 overflow-hidden text-white'>
                <div className='relative z-10 mx-auto flex min-h-full w-full max-w-[1040px] flex-col px-5 py-5 sm:px-8 md:py-8'>
                    <header className='flex justify-center'>
                        <LogosVersion2/>
                    </header>

                    <main className='grid flex-1 auto-rows-max content-center gap-9 pb-[12vh] pt-6 sm:gap-7 sm:pb-[18vh] lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-12 lg:py-8'>
                        <section aria-labelledby='welcome-heading' className='flex flex-col text-center lg:text-left'>
                            <h1 id='welcome-heading' className='mx-auto max-w-[600px] text-[38px] font-bold leading-[37px] sm:text-[44px] sm:leading-[44px] lg:mx-0 lg:max-w-[560px] lg:text-[52px] lg:leading-[52px] lg:tracking-[-0.025em]'>
                                Continue Learning &amp; <span className='text-[#54A3F5]'>3x Your Career</span>
                            </h1>
                            <p className='mx-auto my-3 max-w-[520px] text-center font-medium leading-[19px] lg:mx-0 lg:text-left lg:text-base lg:leading-6'>
                                <span className='opacity-50'>Fast-forward your</span> <span className='opacity-80'>future</span><span className='opacity-50'> with our magical AI powered </span><span className='opacity-80'>hyper-personalised learning!</span>
                            </p>

                        </section>

                        <section className='w-full max-w-[500px] justify-self-center rounded-3xl border border-white/10 bg-gradient-to-br from-[#17395A]/95 to-[#102D47]/95 p-5 shadow-[0_24px_70px_rgba(0,0,0,0.3)] backdrop-blur-xl sm:p-6 lg:max-w-none lg:p-7'>
                            {/* <h2 className='text-2xl font-bold tracking-[-0.02em]'>Continue your course</h2> */}
                            {/* <p className='mt-1.5 text-sm leading-5 text-[#8FB0CB]'>Enter the email you used to create your learning path.</p> */}

                            <form onSubmit={handleSubmit}>
                                <label htmlFor='email' className='text-sm font-semibold text-[#E8F1F7]'>Enter Your Email Address</label>
                                <div className='relative mt-2'>
                                    <Mail size={18} className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${error ? 'text-[#F5C451]' : 'text-[#8FB0CB]'}`} aria-hidden='true'/>
                                    <input
                                        type='email'
                                        inputMode='email'
                                        autoComplete='email'
                                        id='email'
                                        value={email || ''}
                                        onChange={handleInputChange}
                                        aria-invalid={Boolean(error)}
                                        aria-describedby={error ? 'welcome-email-warning' : 'welcome-email-hint'}
                                        className={`welcome-email-input h-12 w-full rounded-xl border bg-[#102D47] py-3 pl-11 pr-4 text-sm text-white outline-none transition-colors duration-200 placeholder:text-[#7897B1] ${error ? 'border-[#F5C451]' : 'border-white/10 hover:border-[#365C7D] focus:border-[#7FC241]'}`}
                                        placeholder='you@example.com'
                                        required
                                    />
                                </div>
                                {error ?
                                    <p id='welcome-email-warning' role='alert' className='mt-2 flex items-start gap-1.5 text-xs leading-4 text-[#F5C451]'><TriangleAlert size={13} className='mt-0.5 shrink-0' aria-hidden='true'/> Use the same email you used to create your courses</p>
                                    : <p id='welcome-email-hint' className='mt-2 flex items-center gap-1.5 text-xs text-[#8FB0CB]'><LockKeyhole size={12} aria-hidden='true'/> Secure access to your saved progress</p>}

                                <button disabled={!email || loading} type='submit' className='welcome-cta mt-4 flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#7FC241] px-4 font-bold text-[#071D2D] transition-colors duration-200 hover:bg-[#91D653] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A9E672] disabled:cursor-not-allowed disabled:opacity-50'>
                                    {loading ? <ClipLoader size={20}/> : <>Continue learning<ArrowRight size={18} aria-hidden='true'/></>}
                                </button>
                                {error ? <button type='button' onClick={handleCreateCourse} className='mx-auto mt-3 block cursor-pointer text-xs font-medium text-[#B7C8D8] underline decoration-white/40 underline-offset-4 transition-colors duration-200 hover:text-white focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A9E672]'>Create a new course</button> : null}
                            </form>

                            <div className='mt-5 hidden border-t border-white/10 pt-4 sm:block'>
                                <div className='flex items-center'>
                                    <img src="https://assets.learntube.ai/files/AI_Syllabus_generator/Group_8604.png?updatedAt=1687502602718" alt='' className='h-auto w-[76px] object-contain'/>
                                    <p className='ml-3 text-xs font-medium leading-4 text-[#B7C8D8]'>
                                        Join <span className='font-bold text-[#8CC6FF]'>{learnerCount} learners</span> making progress today
                                    </p>
                                </div>
                            </div>
                        </section>

                    </main>

                    <footer aria-label='Learner activity' className='flex shrink-0 items-center justify-center pb-1 sm:hidden'>
                        <img src="https://assets.learntube.ai/files/AI_Syllabus_generator/Group_8604.png?updatedAt=1687502602718" alt='' className='h-auto w-[76px] shrink-0 object-contain'/>
                        <p className='ml-3 text-xs font-medium leading-4 text-[#B7C8D8]'>
                            Join <span className='font-bold text-[#8CC6FF]'>{learnerCount} learners</span> making progress today
                        </p>
                    </footer>
                </div>
            </div> :
            <div className='relative flex h-screen flex-col items-center overflow-hidden p-5 text-white'>
                <div className='relative z-10'><LogosVersion2/></div>
                <div className='relative z-10 flex h-full flex-1 flex-col items-center justify-center gap-4'>
                    <SyncLoader color='#7FC241'/>
                    <p className='text-sm font-medium text-[#AFC1CF]'>Opening your learning path…</p>
                </div>
            </div>}
        </>
    )
}

export default Welcome
